import React, { useState, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Notiflix from "notiflix";
import { LockOutlined, UserOutlined, MailOutlined } from "@ant-design/icons";
import {
  Input,
  Row,
  Typography,
  Card,
  Form,
  Spin,
  Col,
  Divider,
  Button,
  Modal,
  List,
  Avatar,
} from "antd";

import TopContent from "../../../components/layouts/TopContent";
import { BOM } from "../../../services/definedMethod";
import cnt from "../../../services/definedConstant";
import "../../../assets/styles/DatePicker.css";
import styles from "../../../assets/styles/styles";
import MainLayout from "../../../components/layouts/MainLayout";
import { apiConfig, labelConfig, staticConfig } from "../security.config";
import BuildPasswordErrorMessage from "../BuildPasswordErrorMessage";
import { encryptPayload } from "../../authentication/EncryptAuth";

const { Text, Title } = Typography;

const UserProfile = (props) => {
  const { userData } = props;
  const { session } = userData;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [useForm] = Form.useForm();
  const [userProfile, setUserProfile] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  useEffect(() => {
    getCurrentUserProfile();
  }, []);

  const getCurrentUserProfile = () => {
    setIsLoading(true);

    const url = `${apiConfig.Users}/${userData?.userId}`;

    let body = {
      session,
    };

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
      } else {
        setUserProfile(res);
        setIsLoading(false);
      }
    });
  };
  const handlePasswordReset = () => {
    let action = "Reset  ";
    let label =
      "password for " + userProfile?.firstname + " " + userProfile?.lastname;

    Notiflix.Confirm.show("Are you sure?", action + label, "Yes", "No", () => {
      const url = `${apiConfig.Users}/${userProfile?.id}`;
      const params = { command: "resetPassword" };
      const body = {
        method: "PUT",
        session,
        params,
      };

      setIsLoading(true);
      BOM.FetchReqAction(body, url, (err, res) => {
        if (err) {
          Modal.error({
            title: "Password Update Failed",
            content: BuildPasswordErrorMessage(err),
            width: 600,
            centered: false,
            style: { top: 400 },
            okButtonProps: {
              style: {
                backgroundColor: "var(--of-purple)",
              },
            },
          });
          setIsLoading(false);
        } else {
          BOM.AlertMsg(
            cnt.SUCCESS,
            "Password changed successfully. Please login again.",
          );

          setIsLoading(false);

          setTimeout(() => {
            navigate("/sign-in");
          }, 1000);
        }
      });
    });
  };
  return (
    <MainLayout isEnableRightSidebar={false}>
      {/* HEADER */}
      <TopContent
        pageTitle="Profile Information"
        pageItems={staticConfig.profileBreadcrumbItems}
      />

      <Spin spinning={isLoading} size="large">
        <div style={{ paddingTop: 20, marginBottom: 50 }}>
          {/* PROFILE DETAILS CARD */}
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
              marginBottom: 24,
            }}
          >
            <Row align="middle" gutter={24}>
              <Col span={16} style={{ display: "flex" }}>
                <Avatar size={50} icon={<UserOutlined />} />
                <div style={{ marginLeft: 15 }}>
                  <Title level={3} style={{ margin: 0 }}>
                    {userProfile?.firstname} {userProfile?.lastname}
                  </Title>
                  <Text type="secondary" style={{ fontSize: "16px" }}>
                    {userProfile?.email}
                  </Text>
                  <br />
                  <Text type="secondary">{userProfile?.officeName}</Text>
                </div>
              </Col>
              <Col span={8} style={{ textAlign: "right" }}>
                <Button
                  type="primary"
                  shape="round"
                  icon={<LockOutlined />}
                  className="of-primaryBtnColor"
                  onClick={handlePasswordReset}
                >
                  Reset Password
                </Button>
              </Col>
            </Row>
            <Divider />

            <Form layout="vertical">
              <Row gutter={24}>
                <Col span={8}>
                  <Form.Item label={<Text strong>Username</Text>}>
                    <Input
                      className="of-bold-disable-input"
                      disabled
                      value={userProfile?.username}
                      prefix={<UserOutlined />}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label={<Text strong>First Name</Text>}>
                    <Input
                      className="of-bold-disable-input"
                      disabled
                      value={userProfile?.firstname}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label={<Text strong>Last Name</Text>}>
                    <Input
                      className="of-bold-disable-input"
                      disabled
                      value={userProfile?.lastname}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col span={8}>
                  <Form.Item label={<Text strong>Email</Text>}>
                    <Input
                      className="of-bold-disable-input"
                      disabled
                      value={userProfile?.email}
                      prefix={<MailOutlined />}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label={<Text strong>Office/Branch</Text>}>
                    <Input
                      className="of-bold-disable-input"
                      disabled
                      value={userProfile?.officeName}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label={<Text strong>Staff</Text>}>
                    <Input
                      className="of-bold-disable-input"
                      disabled
                      value={userProfile?.staff?.displayName || "N/A"}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>

          {/* ROLES CARD */}
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            <Title level={4}>My Roles</Title>
            <Divider />

            {userProfile?.selectedRoles?.length > 0 ? (
              <List
                grid={{ gutter: 16, column: 2 }}
                dataSource={userProfile?.selectedRoles}
                renderItem={(role) => (
                  <List.Item>
                    <Card
                      size="small"
                      style={{
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #e9ecef",
                      }}
                    >
                      <Text strong style={{ textTransform: "capitalize" }}>
                        {role.name}
                      </Text>
                    </Card>
                  </List.Item>
                )}
              />
            ) : (
              <Text type="secondary">No roles assigned</Text>
            )}
          </Card>
        </div>

        {/* PASSWORD CHANGE MODAL */}
        <ChangePasswordModal
          isModalOpen={isPasswordModalOpen}
          setIsModalOpen={setIsPasswordModalOpen}
          session={session}
          userProfile={userProfile}
          navigate={navigate}
        />
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, {})(UserProfile);

// PASSWORD CHANGE MODAL COMPONENT
const ChangePasswordModal = ({
  isModalOpen,
  setIsModalOpen,
  session,
  userProfile,
  navigate,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const onSubmit = (formValues) => {
    const { password, repeatPassword } = formValues;
    if (password !== repeatPassword) {
      BOM.AlertMsg(cnt.DANGER, "New passwords do not match");
      return;
    }

    const url = `${apiConfig.Users}/${userProfile?.id}`;
    const encryptedPassword = encryptPayload(formValues.password);
    const encryptedRepeatPassword = encryptPayload(formValues.repeatPassword);
    const params = { command: "resetPassword" };
    const body = {
      password: encryptedPassword,
      repeatPassword: encryptedRepeatPassword,
      method: "PUT",
      session,
      params,
    };

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        Modal.error({
          title: "Password Update Failed",
          content: BuildPasswordErrorMessage(err),
          width: 600,
          centered: false,
          style: { top: 400 },
          okButtonProps: {
            style: {
              backgroundColor: "var(--of-purple)",
            },
          },
        });
        // BOM.AlertMsg(
        //   cnt.DANGER,
        //   err?.errors[0]?.defaultUserMessage ||
        //     err?.defaultUserMessage ||
        //     "Failed to change password"
        // );
        setIsLoading(false);
      } else {
        BOM.AlertMsg(
          cnt.SUCCESS,
          "Password changed successfully. Please login again.",
        );

        // Clear form
        form.resetFields();
        setIsModalOpen(false);
        setIsLoading(false);

        setTimeout(() => {
          //   localStorage.removeItem("token");
          navigate("/sign-in");
        }, 1000);
      }
    });
  };

  return (
    <Modal
      title="Password Entry"
      open={isModalOpen}
      okText="Submit"
      cancelText="Cancel"
      okButtonProps={{
        autoFocus: true,
        htmlType: "submit",
        style: {
          ...styles.ofPrimaryBtnColor,
        },
      }}
      centered
      onCancel={handleCancel}
      width={800}
      modalRender={(dom) => (
        <Form layout="vertical" form={form} onFinish={onSubmit}>
          {dom}
        </Form>
      )}
      style={{
        height: 600,
        overflow: "auto",
      }}
    >
      <Spin spinning={isLoading} tip="Loading..." size="large">
        <Row style={{ justifyContent: "space-betwen" }}>
          <Divider orientation="left" orientationMargin="0">
            {labelConfig.generalInfo}
          </Divider>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              style={{ width: "100%", marginBottom: "5px" }}
              name={"password"}
              rules={BOM.fieldRules("strong-password", true)}
            >
              <Input.Password
                placeholder={`${labelConfig.password}`}
                style={{ padding: 10, borderRadius: 10 }}
                prefix={<LockOutlined className="site-form-item-icon" />}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              style={{ width: "100%", marginBottom: "5px" }}
              name={"repeatPassword"}
              rules={BOM.fieldRules("strong-password", true)}
            >
              <Input.Password
                placeholder={`${labelConfig.repeatPassword}`}
                style={{ padding: 10, borderRadius: 10 }}
                prefix={<LockOutlined className="site-form-item-icon" />}
              />
            </Form.Item>
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
};
